import { useCallback, useMemo } from 'react';

import { composeChannels } from './composeChannels';
import { useChannelsSettings } from './settings';
import { useChannelAssets } from './useChannelAssets';
import { useChannelCollection } from './useChannelCollection';
import { useEntitledChannels } from './useEntitledChannels';
import { useFavoriteChannelWithInitial } from './useFavoriteChannelWithInitial';

type UseMyFavoriteChannelsProps = {
  referenceId: string;
};

export function useMyFavoriteChannels({ referenceId }: UseMyFavoriteChannelsProps) {
  const { settings, error: settingsError } = useChannelsSettings({ referenceId });

  const { channelAssetsMap } = useChannelAssets();
  const { entitledChannels } = useEntitledChannels();
  const {
    channelCollection,
    error: collectionError,
    isLoading,
    refetch: refetchChannelCollection,
  } = useChannelCollection({ settings });

  const {
    error: favoriteChannelError,
    favoriteChannels,
    initialFavoriteChannels,
    refetch: refetchFavoriteChannelWithInitial,
    reorder,
    isRefetching,
  } = useFavoriteChannelWithInitial();

  const error = settingsError ?? collectionError ?? favoriteChannelError;

  const channels = useMemo(() => {
    if (!settings) {
      return;
    }

    if (!channelAssetsMap || !entitledChannels || !channelCollection) {
      return;
    }

    if (!favoriteChannels || !initialFavoriteChannels) {
      return;
    }

    const sortedChannels = composeChannels({
      channelAssetsMap,
      channelCollection,
      entitledChannels,
      personalization: {
        favoriteChannels: initialFavoriteChannels,
        lastWatched: undefined,
      },
      settings,
    });

    const channelsWithUpdatedFavorites = sortedChannels?.map((channel) => ({
      ...channel,
      isFavorite: favoriteChannels.has(channel.id),
    }));

    return channelsWithUpdatedFavorites;
  }, [channelAssetsMap, channelCollection, entitledChannels, favoriteChannels, initialFavoriteChannels, settings]);

  const refetch = useCallback(async () => {
    await Promise.all([refetchChannelCollection(), refetchFavoriteChannelWithInitial()]);
  }, [refetchChannelCollection, refetchFavoriteChannelWithInitial]);

  return {
    channels,
    error,
    refetch,
    reorder,
    isLoading: isLoading || isRefetching || (!initialFavoriteChannels && !favoriteChannelError),
  };
}
